import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { Store, Path, Query, Reference } from './store';
import { backend } from './backend';
import { RootModel } from './root.model';
import { ApiHttp } from './api-http.service';
import { WebSocketService } from './websocket';
import { ListObservable } from './list-observable';
import { ObjectObservable } from './object-observable';

export interface ListOptions {
  query: QueryObservable;
}

export interface QueryObservable {
  limit?: number | Observable<number>;
  offset?: number | Observable<number>;
}

@Injectable()
export class Datastore {
  constructor(
    private store: Store<RootModel>,
    private apiHttp: ApiHttp,
    private webSocketService: WebSocketService,
  ) {}

  list<a extends keyof RootModel>(
    key1: a,
    options?: ListOptions,
  ): ListObservable<a, RootModel[a]>;
  list<a extends keyof RootModel, b extends keyof RootModel[a]>(
    key1: a,
    key2: b | Observable<b>,
    options?: ListOptions,
  ): ListObservable<a, RootModel[a][b]>;
  list<
    a extends keyof RootModel,
    b extends keyof RootModel[a],
    c extends keyof RootModel[a][b]
  >(
    key1: a,
    key2: b | Observable<b>,
    key3: c | Observable<c>,
    options?: ListOptions,
  ): ListObservable<a, RootModel[a][b][c]>;
  list<T extends keyof RootModel>(
    arg1: T,
    ...args: any[]
  ): ListObservable<T, any> {
    let optionsQuery: QueryObservable = {};
    if (args.length && args[args.length - 1].query) {
      optionsQuery = args.pop().query;
    }
    const args$ = [arg1, ...args].map(
      key => (key instanceof Observable ? key : Observable.of(key)),
    );
    const pathStream = Observable.combineLatest(args$);

    const queryStream = Observable.combineLatest(
      [
        optionsQuery.limit instanceof Observable
          ? optionsQuery.limit
          : Observable.of(optionsQuery.limit),
        optionsQuery.offset instanceof Observable
          ? optionsQuery.offset
          : Observable.of(optionsQuery.offset),
      ],
      (limit: number, offset: number) => ({ limit, offset }),
    );

    const refStream: Observable<Reference<T>> = Observable.combineLatest(
      [pathStream, queryStream],
      (path: Path<T>, query: Query) => ({ path, query }),
    )
      .multicast(() => new ReplaySubject(1))
      .refCount();

    const fetchStream: Observable<Reference<T>> = refStream.switchMap(ref => {
      const { path, query } = ref;
      const fetch = backend[path[0]].fetch;
      if (fetch) {
        return Observable.of(fetch(path[1], query))
          .switchMap(config => this.apiHttp.fetch(config, query))
          .do(data => {
            this.store.dispatch<T>({
              type: 'API_FETCH_SUCCESS',
              payload: { data, ref },
            });
          })
          .mapTo({ path, query });
      } else {
        return Observable.empty();
      }
    });

    const websocketStream: Observable<
      Reference<T>
    > = this.webSocketService.fromServerStream.combineLatest(
      refStream,
      (ws, ref) => ref,
    );

    return new ListObservable(
      refStream,
      this.apiHttp,
      this.store,
      Observable.merge(refStream, fetchStream, websocketStream)
        .switchMap(({ path }) => this.store.pluck(...path))
        .distinctUntilChanged()
        .map((data: any) => {
          if (data && data.lists) {
            return data.lists['default']
              .slice(optionsQuery.offset || 0)
              .map((id: any) => data.entities[<string>id]);
          } else {
            return [];
          }
        }),
    );
  }

  object<a extends keyof RootModel>(key1: a): ObjectObservable<RootModel[a]>;
  object<a extends keyof RootModel, b extends keyof RootModel[a]>(
    key1: a,
    key2: b | Observable<b>,
  ): ObjectObservable<RootModel[a][b]>;
  object<
    a extends keyof RootModel,
    b extends keyof RootModel[a],
    c extends keyof RootModel[a][b]
  >(
    key1: a,
    key2: b | Observable<b>,
    key3: c | Observable<c>,
  ): ObjectObservable<RootModel[a][b][c]>;
  object<T extends keyof RootModel>(
    arg1: T,
    ...args: any[]
  ): ObjectObservable<any> {
    const args$ = [arg1, ...args].map(
      key => (key instanceof Observable ? key : Observable.of(key)),
    );

    const refStream: Observable<Reference<T>> = Observable.combineLatest(args$)
      .map((path: Path<T>) => ({ path }))
      .multicast(() => new ReplaySubject(1))
      .refCount();

    const fetchStream: Observable<
      Reference<T>
    > = refStream.switchMap(({ path }) => {
      if (backend[path[0]].fetch) {
        return Observable.of((<any>backend)[path[0]].fetch(path[1]))
          .switchMap(config => this.apiHttp.fetch(config))
          .do(data => {
            this.store.dispatch<T>({
              type: 'API_FETCH_SUCCESS',
              payload: { data, ref: { path } },
            });
          })
          .mapTo({ path });
      } else {
        return Observable.empty();
      }
    });

    const subscriptionStream: Observable<Reference<T>> =
      arg1 === 'onlineOffline'
        ? this.webSocketService
            .subscribeOnlineOffline(args$[1])
            .combineLatest(refStream, (ss, ref) => ref)
        : refStream;

    const websocketStream: Observable<
      Reference<T>
    > = this.webSocketService.fromServerStream.combineLatest(
      refStream,
      (ws, ref) => ref,
    );

    return new ObjectObservable(
      refStream,
      this.apiHttp,
      this.store,
      Observable.merge(
        refStream,
        fetchStream,
        subscriptionStream,
        websocketStream,
      )
        .switchMap(({ path }) => this.store.pluck(...path))
        .distinctUntilChanged()
        .filter(obj => obj !== undefined),
    );
  }
}



// WEBPACK FOOTER //
// /mnt/jenkins-slave-workspace/workspace/gaf-build/webapp/src/app/@shared/datastore/datastore.ts